import { Component, OnInit, OnChanges, Input } from '@angular/core';
import { BaseComponent } from '../classes/BaseComponent';
import { PriceHelper } from '../helpers/PriceHelper';

@Component({
  selector: 'app-price-information',
  templateUrl: './price-information.component.html',
  styleUrls: ['./price-information.component.scss']
})
export class PriceInformationComponent extends BaseComponent implements OnInit, OnChanges {

  @Input() item: any;
  @Input() currency: string;
  @Input() isToBuy: boolean = true;

  lowestPrice: number = 0;
  currentPrice: number = 0;
  highestPrice: number = 0;

  constructor() {
    super();
  }

  ngOnInit() {
    this.calculatePrices();
  }

  ngOnChanges() {
    this.calculatePrices();
  }

  private calculatePrices() {
    if (!this.item) return;

    // Get current price
    this.currentPrice = this.getCurrentPriceValue();

    // Get price evolution data
    const priceEvolution = this.getPriceEvolution();
    if (priceEvolution) {
      const prices = this.parsePriceEvolution(priceEvolution);
      if (prices.length > 0) {
        this.lowestPrice = Math.min(...prices);
        this.highestPrice = Math.max(...prices);
      } else {
        // If no valid prices found, use current price
        this.lowestPrice = this.currentPrice;
        this.highestPrice = this.currentPrice;
      }
    } else {
      // No evolution data - all prices are the same (initial price only)
      this.lowestPrice = this.currentPrice;
      this.highestPrice = this.currentPrice;
    }
  }

  private getCurrentPriceValue(): number {
    if (!this.item) return 0;

    // Use same logic as PriceHelper.getCurrentDetailViewPrice but return number
    if (PriceHelper.hasSdPrice(this.item, this.isToBuy) && !PriceHelper.hasHdPrice(this.item, this.isToBuy)) {
      return this.isToBuy ? this.item.priceSd : this.item.rentalPriceSd;
    } else if (PriceHelper.hasHdPrice(this.item, this.isToBuy)) {
      return this.isToBuy ? this.item.priceHd : this.item.rentalPriceHd;
    } else if (PriceHelper.has4kPrice(this.item, this.isToBuy)) {
      return this.isToBuy ? this.item.price4k : this.item.rentalPrice4k;
    } else if (PriceHelper.hasPrice(this.item, this.isToBuy)) {
      return this.isToBuy ? this.item.price : this.item.rentalPrice;
    }
    return 0;
  }

  private getPriceEvolution(): string {
    if (!this.item) return '';

    // Use same logic as detail-view component
    if (PriceHelper.hasSdPrice(this.item, this.isToBuy) && !PriceHelper.hasHdPrice(this.item, this.isToBuy)) {
      return this.isToBuy ? this.item.priceSdEvolution : this.item.rentalPriceSdEvolution;
    } else if (PriceHelper.hasHdPrice(this.item, this.isToBuy)) {
      return this.isToBuy ? this.item.priceHdEvolution : this.item.rentalPriceHdEvolution;
    } else if (PriceHelper.has4kPrice(this.item, this.isToBuy)) {
      return this.isToBuy ? this.item.price4kEvolution : this.item.rentalPrice4kEvolution;
    } else if (PriceHelper.hasPrice(this.item, this.isToBuy)) {
      return this.isToBuy ? this.item.priceEvolution : this.item.rentalPriceEvolution;
    }
    return '';
  }

  private parsePriceEvolution(evolution: string): number[] {
    if (!evolution) return [];

    const prices = [];
    const elements = evolution.split('~');

    for (const element of elements) {
      if (element) {
        const parts = element.split(':');
        if (parts.length >= 2) {
          const indicator = parts[1][0];
          let priceStr = parts[1];
          if (indicator === '+' || indicator === '-') {
            priceStr = priceStr.substring(1);
          }
          const price = parseFloat(priceStr);
          if (!isNaN(price)) {
            prices.push(price);
          }
        }
      }
    }
    return prices;
  }

  getLowestPriceFormatted(): string {
    return PriceHelper.formatPrice(this.lowestPrice, this.currency);
  }

  getCurrentPriceFormatted(): string {
    return PriceHelper.formatPrice(this.currentPrice, this.currency);
  }

  getHighestPriceFormatted(): string {
    return PriceHelper.formatPrice(this.highestPrice, this.currency);
  }

  getLowestToCurrentDifference(): string {
    const diff = this.currentPrice - this.lowestPrice;
    if (diff === 0) return '';
    const sign = diff > 0 ? '+' : '';
    return sign + PriceHelper.formatPrice(Math.abs(diff), this.currency);
  }

  getCurrentToHighestDifference(): string {
    const diff = this.highestPrice - this.currentPrice;
    if (diff === 0) return '';
    const sign = diff > 0 ? '-' : '+';
    return sign + PriceHelper.formatPrice(Math.abs(diff), this.currency);
  }

  getLowestPriceColor(): string {
    // If price never changed (all equal) -> gray
    if (this.lowestPrice === this.currentPrice && this.currentPrice === this.highestPrice) {
      return 'gray';
    }
    return 'gold';
  }

  getCurrentPriceColor(): string {
    // Best price (current equals lowest AND flagged as best)
    if (this.currentPrice === this.lowestPrice && PriceHelper.isBestPriceForItem(this.item)) {
      return 'gold';
    }

    // Determine last price movement sign from price evolution
    const evolution = this.getPriceEvolution();
    if (evolution) {
      const firstEntry = evolution.split('~')[0]; // newest
      const parts = firstEntry.split(':');
      if (parts.length >= 2) {
        const priceStr = parts[1];
        if (priceStr.startsWith('-')) {
          return 'green'; // price dropped
        }
        if (priceStr.startsWith('+')) {
          return 'red'; // price increased
        }
      }
    }

    return 'gray';
  }

  getHighestPriceColor(): string {
    if (this.lowestPrice === this.currentPrice && this.currentPrice === this.highestPrice) {
      return 'gray';
    }
    return 'red';
  }

  private getInitialPrice(): number {
    const priceEvolution = this.getPriceEvolution();
    if (!priceEvolution) {
      return this.currentPrice;
    }

    const elements = priceEvolution.split('~');
    let fallbackPrice: number | null = null;

    for (let i = elements.length - 1; i >= 0; i--) {
      const [, raw] = elements[i].split(':');
      if (!raw) continue;
      const clean = raw.replace(/[+-]/g, '');
      const value = parseFloat(clean);
      if (isNaN(value)) continue;
      if (fallbackPrice === null) fallbackPrice = value; // keep last parsed as fallback
      // Initial price is typically sign-less (no + or -)
      if (!raw.startsWith('+') && !raw.startsWith('-')) {
        return value;
      }
    }
    return (fallbackPrice !== null && fallbackPrice !== undefined) ? fallbackPrice : this.currentPrice;
  }

  // ---------- Tooltip helpers ----------
  private formatDate(dateStr: string): string {
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? dateStr : d.toLocaleDateString();
  }

  getLowestTooltip(): string {
    const date = PriceHelper.getFirstReachedDate(this.item, this.lowestPrice);
    const price = this.getLowestPriceFormatted();
    if (date) {
      return this.getString('priceInfoTooltipLowest', { price, date: this.formatDate(date) });
    }
    return this.getString('priceInfoTooltipLowestNoDate', { price });
  }

  getCurrentTooltip(): string {
    const price = this.getCurrentPriceFormatted();
    return this.getString('priceInfoTooltipCurrent', { price });
  }

  getHighestTooltip(): string {
    const date = PriceHelper.getFirstReachedDate(this.item, this.highestPrice);
    const price = this.getHighestPriceFormatted();
    if (date) {
      return this.getString('priceInfoTooltipHighest', { price, date: this.formatDate(date) });
    }
    return this.getString('priceInfoTooltipHighestNoDate', { price });
  }
}
